
import React from "react";

interface CourseDescriptionProps {
  description?: string;
}

const CourseDescription = ({ description }: CourseDescriptionProps) => {
  // If there's no description, return nothing
  if (!description) return null;

  // Split the description into paragraphs
  const paragraphs = description.split('\n\n');

  return (
    <div className="mb-12">
      <h2 className="text-xl font-semibold mb-3">Kursuse kirjeldus</h2>
      <div className="text-gray-700 space-y-4">
        {paragraphs.map((paragraph, index) => {
          // Check if the paragraph contains a list (starts with a dash or bullet)
          if (paragraph.trim().startsWith('-') || paragraph.trim().includes('\n-')) {
            // Split the paragraph into lines
            const lines = paragraph.split('\n');
            
            // Get the potential header (first line if it doesn't start with a dash)
            let header = null;
            let listItems = lines;
            
            if (lines.length > 0 && !lines[0].trim().startsWith('-')) {
              header = lines[0];
              listItems = lines.slice(1);
            }
            
            // Process list items and their descriptions
            const processedItems = [];
            for (let i = 0; i < listItems.length; i++) {
              const line = listItems[i].trim();
              
              // Skip empty lines
              if (line.length === 0) continue;
              
              // If this is a bullet point
              if (line.startsWith('-')) {
                const bulletText = line.substring(1).trim();
                
                // Check if the next line exists and is not a bullet point (description)
                const hasDescription = i + 1 < listItems.length && 
                                       !listItems[i + 1].trim().startsWith('-') && 
                                       listItems[i + 1].trim().length > 0;
                
                if (hasDescription) {
                  // Add both the bullet point and its description
                  processedItems.push({
                    bulletText,
                    description: listItems[i + 1].trim()
                  });
                  
                  // Skip the description line in next iteration
                  i++;
                } else {
                  // Just a bullet point without description
                  processedItems.push({
                    bulletText,
                    description: null
                  });
                }
              }
            }

            return (
              <div key={index}>
                {header && <p className="font-medium mb-2">{header}</p>}
                <ul className="list-disc pl-5 space-y-3">
                  {processedItems.map((item, itemIndex) => (
                    <li key={itemIndex} className="pl-1">
                      <div>{item.bulletText}</div>
                      {item.description && (
                        <div className="mt-1 ml-0 text-gray-600 not-italic">
                          {item.description}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            );
          } else if (paragraph.includes(':') && !paragraph.includes('\n')) {
            // This might be a header or title
            const parts = paragraph.split(':');
            return (
              <div key={index} className="mb-2">
                <strong>{parts[0]}:</strong> {parts.slice(1).join(':')}
              </div>
            );
          } else {
            // Regular paragraph
            return <p key={index}>{paragraph}</p>;
          }
        })}
      </div>
    </div>
  );
};

export default CourseDescription;
